<template>
    <article class="cert-card">
        <div class="cert-card__course">
            <img
                v-if="image"
                class="cert-card__thumb"
                :src="image"
                :alt="name"
            />
            <div v-else class="cert-card__thumb cert-card__thumb--empty" aria-hidden="true">
                <v-icon size="28">mdi-certificate-outline</v-icon>
            </div>
            <div class="cert-card__copy">
                <h2>{{ name }}</h2>
                <p v-if="duration" class="cert-card__meta">
                    <v-icon size="15" aria-hidden="true">mdi-clock-outline</v-icon>
                    {{ duration }}
                </p>
                <CertificateStatus :issued="issued" :date-text="dateText" />
            </div>
        </div>

        <CertificatePending v-if="!issued" :labels="pendingLabels" />
        <div v-else class="cert-card__pending-spacer" aria-hidden="true"></div>

        <CertificateActions
            :issued="issued"
            :eligible="eligible"
            :course-id="courseId"
            :issuing="issuing"
            @view="$emit('view')"
            @generate="$emit('generate')"
            @continue="$emit('continue')"
        />
    </article>
</template>

<script>
    import CertificateStatus from './CertificateStatus.vue';
    import CertificatePending from './CertificatePending.vue';
    import CertificateActions from './CertificateActions.vue';

    export default {
        name: 'CertificateCard',
        components: {
            CertificateStatus,
            CertificatePending,
            CertificateActions,
        },
        props: {
            name: { type: String, default: '' },
            image: { type: String, default: '' },
            duration: { type: String, default: '' },
            issued: { type: Boolean, default: false },
            eligible: { type: Boolean, default: false },
            dateText: { type: String, default: '' },
            pendingLabels: { type: Array, default: () => [] },
            courseId: { type: [String, Number], default: null },
            issuing: { type: Boolean, default: false },
        },
        emits: ['view', 'generate', 'continue'],
    };
</script>

<style scoped>
    .cert-card {
        display: grid;
        grid-template-columns: minmax(280px, 1.3fr) minmax(190px, 1fr) max-content;
        gap: 8px 18px;
        align-items: center;
        padding: 10px 12px;
        border: 1px solid var(--certs-line, #eceef3);
        border-radius: 12px;
        background: var(--certs-surface, #fff);
        transition: border-color 0.18s ease, box-shadow 0.18s ease;
    }

    .cert-card:hover {
        border-color: var(--certs-card-hover, #d5d8e2);
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
    }

    .cert-card__course {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
    }

    .cert-card__thumb {
        width: 96px;
        height: 64px;
        object-fit: cover;
        border-radius: 8px;
        flex-shrink: 0;
        background: #eef2f6;
    }

    .cert-card__thumb--empty {
        display: grid;
        place-items: center;
        color: #0f766e;
        background: rgba(26, 196, 205, 0.12);
    }

    .cert-card__copy {
        min-width: 0;
        flex: 1;
        text-align: left;
    }

    .cert-card__copy h2 {
        margin: 0;
        font-size: 0.98rem;
        font-weight: 700;
        text-align: left;
        color: var(--certs-text, #111827);
        line-height: 1.3;
    }

    .cert-card__copy :deep(.cert-status) {
        margin-top: 8px;
    }

    .cert-card__meta {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 6px 0 0;
        color: var(--certs-muted, #6b7280);
        font-size: 0.84rem;
    }

    .cert-card__pending-spacer {
        min-height: 1px;
    }

    @media (max-width: 860px) {
        .cert-card {
            grid-template-columns: 1fr;
            align-items: stretch;
        }

        .cert-card__course {
            align-items: flex-start;
        }

        .cert-card__thumb {
            width: 88px;
            height: 60px;
        }
    }
</style>
